import { useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "./firebaseio/AuthContext";
import { LandingPage } from "./website/LandingPage";
import { LoggedIn } from "./LoggedIn";
import { hasSessionHint } from "./auth/sessionHint";
import { startLoadingFonts } from "./fontsReady";

// Handles "/" plus every app path (the main nav tabs and anything unrecognized).
//
// These deliberately share ONE route element. "/" and "/transactions" resolve to
// the same mounted component, so moving between them keeps <LoggedIn /> mounted.
// Splitting them across two routes remounts the whole app tree on the first tab
// click after sign-in, which re-runs SubscriptionCheck and replays the full-page
// loading screen.
export function RootRoute(): JSX.Element {
  const auth = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  const isRootPath = location.pathname === "/";

  useEffect(() => {
    // Signed-out visitors on an app path go to the landing page. AuthContext has
    // cleared the session hint by now, so "/" renders marketing and the redirect
    // settles instead of bouncing.
    if (auth === null && !isRootPath) {
      navigate("/", { replace: true });
    }
  }, [auth, isRootPath, navigate]);

  // Decided synchronously, before Firebase has reported anything. This is what
  // puts the landing page in the very first render pass, so prerendering and
  // crawlers capture real marketing content instead of an empty div.
  //
  // The !auth term is the fallback for browsers where localStorage is
  // unavailable (private mode), so the hint never persists: such a user briefly
  // sees marketing and then lands in the app once Firebase resolves, rather than
  // being stuck on the landing page.
  if (isRootPath && !auth && !hasSessionHint()) {
    return <LandingPage />;
  }

  // The app is about to render, so get its fonts downloading while Firebase
  // confirms the session.
  startLoadingFonts();

  // A session should exist, so wait for confirmation rather than flashing the
  // landing page at someone who is already signed in.
  if (auth === undefined) {
    return <div />;
  }

  // Hint was stale (expired token, or signed out in another tab). AuthContext
  // has already cleared it.
  if (auth === null) {
    return isRootPath ? <LandingPage /> : <div />;
  }

  return <LoggedIn />;
}
